import type { ReactElement } from "react";
import { Copy } from "lucide-react";
import {
  Button,
  DescriptionItem,
  DescriptionList,
  RightDrawer,
  toast,
} from "@playbookai/ui";
import type { DateTimeFormatter } from "../../../settings/format-date-time.js";
import type { AuditEvent } from "../../../api/admin-audit-api.js";
import {
  actionLabel,
  entityReference,
  entityTypeLabel,
  payloadEntries,
  serviceLabel,
} from "../audit-format.js";

/** The artboard's Event details drawer. */
const EVENT_DRAWER_WIDTH = 460;

/** What the drawer shows and what Copy as JSON writes: ONE model (PB-5216,
    d6), in the CSV export's vocabulary — timestamp, action, service, actor,
    actor email, affected type, affected, reference, detail — each value as
    the page renders it. Never the row's ids and never the raw payload: the
    reference-only rule of `audit-format.ts` holds for a clipboard exactly as
    it does for a cell, since pasted JSON is a screenshot nobody can take
    back. The one difference from the file is the zone: the CSV says UTC in
    its header, the screen uses the reader's zone, and `when` names it. */
export interface EventDisplayModel {
  readonly when: string;
  readonly action: string;
  /** The product service that wrote the event. */
  readonly service?: string;
  readonly actor: string;
  readonly actorEmail: string | null;
  readonly affectedType: string;
  readonly affected: string;
  readonly reference: string;
  /** Label/value pairs, not an object: `sentenceCase` is many-to-one, so two
      raw keys can share a label, and an object would drop one of them. */
  readonly detail: ReadonlyArray<{
    readonly label: string;
    readonly value: string;
  }>;
}

export function eventDisplayModel(
  event: AuditEvent,
  formatter: DateTimeFormatter,
): EventDisplayModel {
  const date = formatter.date(event.ts);
  const time = formatter.time(event.ts);
  const type = entityTypeLabel(event.entityType);
  return {
    when:
      date && time ? `${date}, ${time} (${formatter.timeZoneLabel})` : event.ts,
    action: actionLabel(event.action),
    ...("service" in event ? { service: serviceLabel(event.service) } : {}),
    /* The same three cases as `ActorCell`, and the same words as the CSV. */
    actor:
      event.actorName ??
      (event.actorId
        ? `Former member · ${entityReference(event.actorId)}`
        : "System"),
    actorEmail: event.actorEmail,
    affectedType: type,
    affected: ("entityName" in event ? event.entityName : null) ?? type,
    reference: entityReference(event.entityId),
    detail: payloadEntries(event.payload, formatter.timeZone).map((entry) => ({
      label: entry.key,
      value: entry.value,
    })),
  };
}

/** The Event details drawer (PB-5216, d5/d6): the export vocabulary as
    label/value rows, then the detail entries, and Copy as JSON in the footer.
    `event` outlives `open` so the panel keeps its content through the close
    motion instead of emptying mid-slide. */
export function EventDrawer({
  open,
  event,
  formatter,
  onClose,
}: {
  open: boolean;
  event: AuditEvent | null;
  formatter: DateTimeFormatter;
  onClose: () => void;
}): ReactElement {
  const model = event ? eventDisplayModel(event, formatter) : null;
  const rows: ReadonlyArray<readonly [string, string]> = model
    ? [
        ["When", model.when],
        ["Action", model.action],
        ...(model.service !== undefined
          ? [["Source", model.service] as const]
          : []),
        ["Actor", model.actor],
        ...(model.actorEmail
          ? [["Actor email", model.actorEmail] as const]
          : []),
        ["Affected type", model.affectedType],
        ["Affected", model.affected],
        ["Reference", model.reference],
      ]
    : [];

  async function copy(): Promise<void> {
    if (!model) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(model, null, 2));
      toast("Event copied as JSON.", { tone: "healthy" });
    } catch {
      /* Said, not swallowed: a Copy that does nothing reads as a broken
         button, and the clipboard is refused in more contexts than one
         (no secure context, a denied permission, no focus). */
      toast("Could not copy the event: the browser refused clipboard access.", {
        tone: "critical",
      });
    }
  }

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title="Event details"
      width={EVENT_DRAWER_WIDTH}
      footer={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void copy()}
        >
          <Copy className="size-4" aria-hidden="true" />
          Copy as JSON
        </Button>
      }
    >
      {model ? (
        <div className="p-5 text-body-small">
          {/* Look row OK-25: the label/value rows are the DescriptionList. */}
          <DescriptionList layout="justified" divided>
            {rows.map(([label, value]) => (
              <DescriptionItem key={label} label={label}>
                {label === "Reference" ? (
                  <span className="pb-tabular font-mono text-dense-1">
                    {value}
                  </span>
                ) : (
                  value
                )}
              </DescriptionItem>
            ))}
          </DescriptionList>
          <h3 className="mb-1 mt-5 text-dense-2 font-semibold uppercase tracking-wider text-pb-text-muted">
            Detail
          </h3>
          {model.detail.length === 0 ? (
            <p className="py-2 text-pb-text-muted">
              Nothing recorded beyond the event itself.
            </p>
          ) : (
            <DescriptionList layout="justified" divided>
              {model.detail.map((entry, index) => (
                <DescriptionItem
                  key={`${entry.label}-${index}`}
                  label={entry.label}
                >
                  {entry.value}
                </DescriptionItem>
              ))}
            </DescriptionList>
          )}
        </div>
      ) : null}
    </RightDrawer>
  );
}
